import { useEffect, useRef, useState } from "react";
import { Bell, BellRing, Loader2, Apple, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { surfbreaks, type Surfbreak } from "@/data/surfbreaks";
import { fetchConditions, conditionRating } from "@/lib/surf";
import { toast } from "@/hooks/use-toast";

type Settings = {
  enabled: boolean;
  minWave: number; // m
  maxWind: number; // km/h
  spotIds: string[];
};

const KEY = "surf-alerts:v1";
const NOTIFIED_KEY = "surf-alerts:notified";
const POLL_MS = 30 * 60 * 1000; // 30 min

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { enabled: false, minWave: 1.2, maxWind: 20, spotIds: [surfbreaks[0].id] };
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isInstalledPWA(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

function iOSSupportsNotifications(): boolean {
  if (!isIOS()) return false;
  const match = navigator.userAgent.match(/OS (\d+)_/);
  if (!match) return false;
  return parseInt(match[1]) >= 16;
}

// ─── Send notification via service worker (works on Android PWA) ──────────────
async function sendNotification(title: string, body: string, tag: string) {
  // Prefer service worker showNotification (works when app is backgrounded)
  if ("serviceWorker" in navigator) {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      await reg.showNotification(title, {
        body,
        icon: "/icons/icon-192x192.png",
        badge: "/icons/icon-96x96.png",
        tag,
        requireInteraction: false,
      });
      return;
    }
  }
  // Fallback to direct Notification API (tab must be open)
  new Notification(title, {
    body,
    icon: "/icons/icon-192x192.png",
    tag,
  });
}

export function SurfAlerts() {
  const [s, setS] = useState<Settings>(loadSettings);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );
  const [checking, setChecking] = useState(false);
  const [swReady, setSwReady] = useState(false);
  const timer = useRef<number | null>(null);

  const ios = isIOS();
  const installedPWA = isInstalledPWA();
  const iosCanNotify = iOSSupportsNotifications() && installedPWA;
  const supported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    (!ios || iosCanNotify);

  // ── Register service worker ──────────────────────────────────────────────────
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          setSwReady(true);
          console.log("[Barrelcast] SW registered:", reg.scope);
        })
        .catch((err) => {
          console.warn("[Barrelcast] SW registration failed:", err);
          // Still allow alerts via direct Notification fallback
          setSwReady(false);
        });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(s));
  }, [s]);

  const requestPerm = async () => {
    if (!supported) return;
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === "granted") {
      toast({ title: "Alerts enabled", description: "We'll ping you when it lights up." });
      // Send a test notification immediately so user knows it worked
      await sendNotification(
        "🌊 Barrelcast alerts active",
        "You'll be notified when your watched spots fire.",
        "barrelcast-test"
      );
    } else {
      toast({
        title: "Notifications blocked",
        description: "Enable notifications in your browser settings for barrelcast.surf.",
        variant: "destructive",
      });
    }
  };

  const checkNow = async () => {
    if (!s.enabled || permission !== "granted" || s.spotIds.length === 0) return;
    setChecking(true);

    let notified: Record<string, number> = {};
    try {
      notified = JSON.parse(localStorage.getItem(NOTIFIED_KEY) || "{}");
    } catch {}

    const now = Date.now();
    const cooldown = 6 * 60 * 60 * 1000; // 6 hours between alerts per spot

    for (const id of s.spotIds) {
      const spot = surfbreaks.find((b) => b.id === id);
      if (!spot) continue;
      try {
        const data = await fetchConditions(spot);
        const c = data.current;
        const meetsThreshold = c.waveHeight >= s.minWave && c.windSpeed <= s.maxWind;
        const cooldownPassed = !notified[id] || now - notified[id] > cooldown;

        if (meetsThreshold && cooldownPassed) {
          const r = conditionRating(c);
          await sendNotification(
            `🌊 ${spot.name} is ${r.label.toLowerCase()}`,
            `${c.waveHeight.toFixed(1)}m @ ${c.swellPeriod.toFixed(0)}s · wind ${Math.round(c.windSpeed)} km/h. Go surf.`,
            `surf-${id}`
          );
          notified[id] = now;
        }
      } catch {
        // ignore individual spot failures silently
      }
    }

    localStorage.setItem(NOTIFIED_KEY, JSON.stringify(notified));
    setChecking(false);
  };

  // ── Polling interval ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    if (s.enabled && permission === "granted") {
      checkNow(); // immediate check on enable
      timer.current = window.setInterval(checkNow, POLL_MS);
    }
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.enabled, permission, s.minWave, s.maxWind, s.spotIds.join(",")]);

  const toggleSpot = (b: Surfbreak) => {
    setS((prev) => ({
      ...prev,
      spotIds: prev.spotIds.includes(b.id)
        ? prev.spotIds.filter((x) => x !== b.id)
        : [...prev.spotIds, b.id],
    }));
  };

  const showIOSInstallPrompt = ios && !installedPWA;
  const showIOSOldVersion = ios && installedPWA && !iOSSupportsNotifications();

  return (
    <article className="bg-card/80 backdrop-blur border border-border p-4 sm:p-6 md:p-8">
      <div className="flex items-start justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">
            {s.enabled && permission === "granted"
              ? <BellRing className="h-3.5 w-3.5 shrink-0 text-primary" />
              : <Bell className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate">Surf alerts</span>
          </div>
          <h3 className="font-display text-xl sm:text-2xl md:text-3xl mt-1 text-foreground leading-tight">
            Get pinged when it fires
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-md">
            We'll check your watch-list every 30 minutes and notify you when a spot hits your thresholds.
            {!swReady && " Keep this tab open for alerts to run."}
          </p>
        </div>
        <Switch
          className="shrink-0 mt-1"
          checked={s.enabled && permission === "granted"}
          disabled={!supported}
          onCheckedChange={async (v) => {
            if (v && permission !== "granted") await requestPerm();
            setS((p) => ({ ...p, enabled: v }));
          }}
        />
      </div>

      {/* iOS — not installed as PWA */}
      {showIOSInstallPrompt && (
        <div className="mt-4 border border-border bg-card/60 p-4 flex gap-3 items-start">
          <Apple className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground">Add to Home Screen to enable alerts</p>
            <p className="text-xs text-muted-foreground mt-1">
              On iPhone, tap the <span className="font-semibold">Share</span> button in Safari, then{" "}
              <span className="font-semibold">Add to Home Screen</span>. Open from your home screen and alerts will work.
            </p>
          </div>
        </div>
      )}

      {/* iOS — installed but old version */}
      {showIOSOldVersion && (
        <div className="mt-4 border border-amber-500/30 bg-amber-500/10 p-4 flex gap-3 items-start">
          <Info className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground">Update iOS to enable alerts</p>
            <p className="text-xs text-muted-foreground mt-1">
              Push notifications require iOS 16.4 or later.{" "}
              <span className="font-semibold">Settings → General → Software Update</span>.
            </p>
          </div>
        </div>
      )}

      {/* Browser doesn't support notifications */}
      {!supported && !ios && (
        <p className="mt-4 text-sm text-destructive">
          This browser doesn't support notifications.
        </p>
      )}

      {/* Notifications blocked */}
      {supported && permission === "denied" && (
        <div className="mt-4 border border-destructive/30 bg-destructive/10 p-4 flex gap-3 items-start">
          <Info className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-destructive">Notifications blocked</p>
            <p className="text-xs text-muted-foreground mt-1">
              {ios
                ? "Go to Settings → Barrelcast → Notifications and enable them."
                : "Click the lock icon in your address bar and allow notifications for barrelcast.surf, then reload."}
            </p>
          </div>
        </div>
      )}

      {/* Thresholds */}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
            Min wave height:{" "}
            <span className="text-foreground">{s.minWave.toFixed(1)} m</span>
          </label>
          <Slider
            min={0.3}
            max={4}
            step={0.1}
            value={[s.minWave]}
            onValueChange={(v) => setS((p) => ({ ...p, minWave: v[0] }))}
            className="mt-3"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
            Max wind:{" "}
            <span className="text-foreground">{Math.round(s.maxWind)} km/h</span>
          </label>
          <Slider
            min={5}
            max={50}
            step={1}
            value={[s.maxWind]}
            onValueChange={(v) => setS((p) => ({ ...p, maxWind: v[0] }))}
            className="mt-3"
          />
        </div>
      </div>

      {/* Watch-list */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">
          Watch-list ({s.spotIds.length})
        </div>
        <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
          {surfbreaks.map((b) => {
            const on = s.spotIds.includes(b.id);
            return (
              <button
                key={b.id}
                onClick={() => toggleSpot(b)}
                className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                  on
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background/40 text-muted-foreground border-border hover:border-primary/40"
                }`}
              >
                {b.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <Button
          onClick={checkNow}
          disabled={!s.enabled || permission !== "granted" || checking}
          variant="secondary"
          className="w-full sm:w-auto"
        >
          {checking
            ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
            : <Bell className="h-4 w-4 mr-2" />}
          Check now
        </Button>
        {permission !== "granted" && supported && (
          <Button
            onClick={requestPerm}
            variant="default"
            className="w-full sm:w-auto"
          >
            Enable notifications
          </Button>
        )}
      </div>
    </article>
  );
}
